import React, { useCallback, useEffect, useState } from 'react';
import {
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  Paper,
} from '@mui/material';

import PageHeader from '../../components/PageHeader';
import {
  permissionsList,
  permissionsWithAssignedIds,
} from '../../api/constant';
import { toast } from 'sonner';
import ProtectedComponent from '../../common/ProtectedComponent';
import { exportKpiTableToExcel, fetchKpiTable } from '../../api/kpiApi';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import KpiComparison from './KpiComparison';

const colors = {
  green: '#4CAF50',
  orange: '#FFC107',
  red: '#F44336',
};

const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const parameters = [
  {
    name: '% OCC RECOVERED',
    competency: '≥ 80%',
    benchmark: '≥ 95%',
    key: 'occRecovered',
    color: (value) =>
      value >= 95 ? colors.green : value >= 80 ? colors.orange : colors.red,
  },
  {
    name: 'MATURATION RATE',
    competency: '≥ 75%',
    benchmark: '≥ 90%',
    key: 'maturationRate',
    color: (value) =>
      value >= 90 ? colors.green : value >= 75 ? colors.orange : colors.red,
  },
  {
    name: 'ICSI NORMAL FERT. RATE',
    competency: '≥ 65%',
    benchmark: '≥ 80%',
    key: 'isciNormalRate',
    color: (value) =>
      value >= 80 ? colors.green : value >= 65 ? colors.orange : colors.red,
  },
  {
    name: 'ICSI DAMAGE RATE',
    competency: '≤ 10%',
    benchmark: '≤ 5%',
    key: 'isciDamageRate',
    color: (value) =>
      value <= 5 ? colors.green : value <= 10 ? colors.orange : colors.red,
  },
  {
    name: '1PN rate (ICSI)',
    competency: '',
    benchmark: '< 3',
    key: 'isciOnePnRate',
    color: (value) => (value <= 3 ? colors.green : colors.red),
  },
  {
    name: 'IVF NORMAL FERT. RATE',
    competency: '≥ 60%',
    benchmark: '≥ 75%',
    key: 'ivfNormalRate',
    color: (value) =>
      value >= 75 ? colors.green : value >= 60 ? colors.orange : colors.red,
  },
  {
    name: 'IVF Polyspermy Rate ',
    competency: '',
    benchmark: '< 6',
    key: 'ivfPolyspermyRate',
    color: (value) => (value <= 6 ? colors.green : colors.orange),
  },
  {
    name: '1PN rate (IVF)',
    competency: '',
    benchmark: '< 5',
    key: 'ivfOnePnRate',
    color: (value) => (value <= 5 ? colors.green : colors.orange),
  },
  {
    name: 'Cleavage Rate',
    competency: '≥ 95',
    benchmark: '≥ 99',
    key: 'cleavageRate',
    color: (value) =>
      value >= 99 ? colors.green : value >= 95 ? colors.orange : colors.red,
  },
  {
    name: 'Day 3 Embryo Development Rate',
    competency: '≥ 45',
    benchmark: '≥ 70',
    key: 'day3DevelopmentRate',
    color: (value) =>
      value >= 70 ? colors.green : value >= 45 ? colors.orange : colors.red,
  },
  {
    name: 'Blastocyst Development Rate',
    competency: '≥ 40',
    benchmark: '≥ 60',
    key: 'blastocystDevelopmentRate',
    color: (value) =>
      value >= 60 ? colors.green : value >= 40 ? colors.orange : colors.red,
  },
  {
    name: 'Good Blastocyst Development Rate',
    competency: '≥ 30',
    benchmark: '≥ 40',
    key: 'goodBlastocystDevelopmentRate',
    color: (value) =>
      value >= 40 ? colors.green : value >= 30 ? colors.orange : colors.red,
  },
  {
    name: 'Blastocyst Cryo-Survival Rate',
    competency: '≥ 90',
    benchmark: '≥ 99',
    key: 'blastocystCryoRate',
    color: (value) =>
      value >= 99 ? colors.green : value >= 90 ? colors.orange : colors.red,
  },
  {
    name: 'Overall Survival Rate',
    competency: '≥ 85',
    benchmark: '≥ 95',
    key: 'overallSurvivalRate',
    color: (value) =>
      value >= 95 ? colors.green : value >= 85 ? colors.orange : colors.red,
  },
  {
    name: 'B-HCG POSITIVE Rate',
    competency: '≥ 50',
    benchmark: '≥ 65',
    key: 'bhcgPositiveRate',
    color: (value) =>
      value >= 65 ? colors.green : value >= 50 ? colors.orange : colors.red,
  },
  {
    name: 'Fresh ET B-HCG POSITIVE Rate',
    competency: '≥ 50',
    benchmark: '≥ 65',
    key: 'freshEtBhcgPositiveRate',
    color: (value) =>
      value >= 65 ? colors.green : value >= 50 ? colors.orange : colors.red,
  },
  {
    name: 'FET Biochemical Pregnancy Rate',
    competency: '≤ 10',
    benchmark: '≤ 5',
    key: 'fetBiochemicalPregnancyRate',
    color: (value) =>
      value <= 5 ? colors.green : value <= 10 ? colors.orange : colors.red,
  },
  {
    name: 'OPU Biochemical Pregnancy Rate',
    competency: '≤ 10',
    benchmark: '≤ 5',
    key: 'opuBiochemicalPregnancyRate',
    color: (value) =>
      value <= 5 ? colors.green : value <= 10 ? colors.orange : colors.red,
  },
  {
    name: 'FET Implantation Rate (Cleavage Stage)',
    competency: '≥ 25',
    benchmark: '≥ 35',
    key: 'fetImplantationRateCleavage',
    color: (value) =>
      value >= 35 ? colors.green : value >= 25 ? colors.orange : colors.red,
  },
  {
    name: 'FET Implantation Rate (Blastocyst Stage)',
    competency: '≥ 35',
    benchmark: '≥ 60',
    key: 'fetImplantationRateBlastocyst',
    color: (value) =>
      value >= 60 ? colors.green : value >= 35 ? colors.orange : colors.red,
  },
  {
    name: 'OPU Implantation Rate (Cleavage Stage)',
    competency: '≥ 25',
    benchmark: '≥ 35',
    key: 'opuImplantationRateCleavage',
    color: (value) =>
      value >= 35 ? colors.green : value >= 25 ? colors.orange : colors.red,
  },
  {
    name: 'OPU Implantation Rate (Blastocyst Stage)',
    competency: '≥ 35',
    benchmark: '≥ 60',
    key: 'opuImplantationRateBlastocyst',
    color: (value) =>
      value >= 60 ? colors.green : value >= 35 ? colors.orange : colors.red,
  },
  {
    name: 'FET Clinical Pregnancy Rate',
    competency: '≥ 40',
    benchmark: '≥ 50',
    key: 'fetClinicalPregnancyRate',
    color: (value) =>
      value >= 50 ? colors.green : value >= 40 ? colors.orange : colors.red,
  },
  {
    name: 'OPU-Fresh Clinical Pregnancy Rate',
    competency: '≥ 40',
    benchmark: '≥ 50',
    key: 'opuFreshClinicalPregnancyRate',
    color: (value) =>
      value >= 50 ? colors.green : value >= 40 ? colors.orange : colors.red,
  },
  {
    name: 'Sperm motility post-preparation (for IUI) ',
    competency: '90',
    benchmark: '≥ 95',
    key: 'spermMotility',
    color: (value) =>
      value >= 95 ? colors.green : value >= 90 ? colors.orange : colors.red,
  },
  {
    name: 'FET Live Birth Rate',
    competency: '≥ 30',
    benchmark: '≥ 50',
    key: 'fetLiveBirthRate',
    color: (value) =>
      value >= 50 ? colors.green : value >= 30 ? colors.orange : colors.red,
  },
  {
    name: 'Live Birth Rate',
    competency: '≥ 30',
    benchmark: '≥ 50',
    key: 'liveBirthRate',
    color: (value) =>
      value >= 50 ? colors.green : value >= 30 ? colors.orange : colors.red,
  },
  {
    name: 'Successful biopsy rate',
    competency: '≥ 90',
    benchmark: '≥ 95',
    key: 'successfulBiopsyRate',
    color: (value) =>
      value >= 95 ? colors.green : value >= 90 ? colors.orange : colors.red,
  },
  {
    name: 'Embryo Utilisation Rate',
    competency: '≥ 40',
    benchmark: '≥ 60',
    key: 'embryoUtilisationRate',
    color: (value) =>
      value >= 60 ? colors.green : value >= 40 ? colors.orange : colors.red,
  },
  {
    name: 'Day 2 Embryo Development Rate',
    competency: '≥ 50',
    benchmark: '≥ 80',
    key: 'day2EmbryoDevelopmentRate',
    color: (value) =>
      value >= 80 ? colors.green : value >= 50 ? colors.orange : colors.red,
  },
];

export default function KpiLanding() {
  const [columns, setColumns] = useState([]);
  const [dates, setDates] = useState({
    startDate: null,
    endDate: null,
  });

  const [compareYears, setCompareYears] = useState(false);

  const exportHandler = () => {
    try {
      const responsePromise = exportKpiTableToExcel(
        dates.startDate,
        dates.endDate,
      );
      toast.promise(
        responsePromise.then((response) => {
          console.log(response);
          if (response.data?.data) {
            const link = document.createElement('a');
            link.href = response.data.data;
            link.download = `kpi-table.csv`; // default file name here
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          return response;
        }),
        {
          loading: 'Downloading report...',
          success: 'File downloaded successfully!',
          error: 'Failed to download report',
        },
      );
    } catch (error) {
      console.error('Error downloading report:', error);
    }
  };

  const applyDate = () => {
    if (!dates.startDate || !dates.endDate) {
      toast.error('Please select both start and end date');
      return;
    }

    const isNegative = dates.startDate.isAfter(dates.endDate);

    if (isNegative) {
      toast.error('Start date cannot be after end date');
      return;
    }

    const moreThanYear =
      Math.abs(dates.startDate.diff(dates.endDate, 'months')) > 12;

    if (moreThanYear) {
      toast.error('Date range cannot be more than 12 months');
      return;
    }
    fetchTable();
  };

  const resetDate = () => {
    setDates({
      startDate: null,
      endDate: null,
    });
    fetchTable();
  };

  const fetchTable = useCallback(() => {
    toast.promise(
      fetchKpiTable(dates.startDate, dates.endDate).then((response) => {
        console.log(response);
        if (response.data.length === 0) {
          toast.error('No data found for the selected date range');
        } else {
          setColumns(response.data);
        }
      }),
      {
        loading: 'Loading ...',
        success: 'Data loaded',
        error: 'Failed to load data',
      },
    );
  }, [dates]);

  useEffect(() => {
    fetchTable();
  }, []);

  return (
    <ProtectedComponent
      requiredPermission={permissionsWithAssignedIds[permissionsList.AddRole]}
    >
      <div className="theme-blush component-wrapper">
        <div className="flex" style={{ alignItems: 'flex-start' }}>
          <div
            className="container-fluid"
            style={{ flex: '1 0 0', justifyContent: 'space-between' }}
          >
            <PageHeader
              HeaderText="KPI Dashboard"
              Breadcrumb={[{ name: 'KPI Dashboard', navigate: '/kpi' }]}
            />
            {/* <div
              className="flex"
              style={{ alignItems: 'center', height: '100%' }}
            >
              Current Notification Days : {notificationDays}
              <IconButton
                onClick={() => {
                  setModalOpen({
                    open: true,
                    title: 'Update Notification Days',
                    body: 'Enter days (greater than 0)',
                    proceedText: 'Update',
                    cancelText: 'Cancel',
                    needsComments: true,
                    isNumber: true,
                    existingMessage: notificationDays,
                    onSave: (days) => {
                      handleUpdateNotificationDays(days);
                    },
                    onClose: () => {
                      setModalOpen({ ...emptyModalState });
                    },
                  });
                }}
                color="primary"
                aria-label="update"
              >
                <i className="fa fa-pencil"></i>
              </IconButton>
            </div> */}
          </div>
        </div>

        {!compareYears ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <DatePicker
                  value={dates.startDate}
                  onChange={(date) => setDates({ ...dates, startDate: date })}
                  picker="month"
                  placeholder="From (Month)"
                />

                <DatePicker
                  value={dates.endDate}
                  onChange={(date) => setDates({ ...dates, endDate: date })}
                  picker="month"
                  placeholder="To (Month)"
                />

                <button
                  className="btn btn-primary ml-2"
                  onClick={() => {
                    applyDate();
                  }}
                >
                  Search
                </button>

                <button
                  className="btn btn-primary ml-2"
                  onClick={() => {
                    resetDate();
                  }}
                >
                  Reset
                </button>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <button
                  className="btn btn-primary ml-2"
                  onClick={() => {
                    setCompareYears(true);
                  }}
                >
                  Compare Years
                </button>

                <button
                  className="btn btn-primary ml-2"
                  onClick={() => exportHandler()}
                >
                  Export to Excel
                </button>
              </div>
            </div>

            <TableContainer component={Paper}>
              <Table style={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '120px' }}>
                      <strong>Parameter</strong>
                    </TableCell>
                    <TableCell style={{ width: '120px' }}>
                      <strong>Competency</strong>
                    </TableCell>
                    <TableCell style={{ width: '120px' }}>
                      <strong>Benchmark</strong>
                    </TableCell>

                    {columns.map((column) => (
                      <TableCell key={column.id}>
                        <strong>
                          {months[column.month - 1]} {column.year}
                        </strong>
                      </TableCell>
                    ))}
                    {columns.length > 0 && (
                      <TableCell>
                        <strong>Average</strong>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parameters.map((parameterGroup, index) => (
                    <TableRow key={index}>
                      <TableCell style={{ width: '20px' }}>
                        <strong>{parameterGroup.name}</strong>
                      </TableCell>
                      <TableCell style={{ width: '20px' }}>
                        <strong>{parameterGroup.competency}</strong>
                      </TableCell>
                      <TableCell style={{ width: '20px' }}>
                        <strong>{parameterGroup.benchmark}</strong>
                      </TableCell>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          style={{
                            backgroundColor: parameterGroup.color(
                              column[parameterGroup.key],
                            ),
                          }}
                        >
                          {column[parameterGroup.key]}
                        </TableCell>
                      ))}
                      {columns.length > 0 && (
                        <TableCell>
                          <strong>
                            {(
                              columns.reduce(
                                (acc, column) =>
                                  acc + column[parameterGroup.key],
                                0,
                              ) / columns.length
                            ).toFixed(2)}
                          </strong>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <KpiComparison
            compareYears={compareYears}
            setCompareYears={setCompareYears}
          />
        )}
        {/* 
        <Pagination
          count={notifications.totalPages}
          page={page.page}
          onChange={handlePageChange}
          color="primary"
          style={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'center',
          }}
        />
        <FormControl
          variant="outlined"
          style={{ minWidth: 120, marginBottom: '20px' }}
        >
          <InputLabel id="page-size-label">Page Size</InputLabel>
          <Select
            labelId="page-size-label"
            id="page-size-select"
            value={page.pageSize}
            onChange={handlePageSizeChange}
            label="Page Size"
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={15}>15</MenuItem>
            <MenuItem value={20}>20</MenuItem>
          </Select>
        </FormControl> */}
      </div>
    </ProtectedComponent>
  );
}
